// src/views/Seguridad/Usuarios/CrearUsuarios.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import Overlay from "../../../utils/overlay"; // solo para error de carga de roles

const PAGE_SIZE = 500;      // cliente / eess
const TARJ_PAGE_SIZE = 200; // tarjeta

/* ====================== utils ====================== */
function useDebounced(v, delay = 300) {
  const [val, setVal] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setVal(v), delay);
    return () => clearTimeout(t);
  }, [v, delay]);
  return val;
}

const isAbort = (e) =>
  e?.name === "AbortError" || /aborted|abort/i.test(e?.message || "");

/* ================ Componente Autocomplete (paginado, con id/label) ================ */
function AutoCombo({
  value,            // string: label visible
  onChange,         // (item | null) => void  (item = { id, label })
  fetchPage,        // async (q, page, signal) => { items: [{id,label}], hasMore: boolean }
  placeholder = "",
  className = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState(value ?? "");
  const debQ = useDebounced(q, 300);

  const boxRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    setQ(value ?? "");
  }, [value]);

  const cancel = () => {
    try {
      abortRef.current?.abort();
    } catch {}
  };

  useEffect(() => {
    const h = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) return;
    cancel();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    (async () => {
      try {
        const { items: list, hasMore } = await fetchPage(
          (debQ ?? "").trim(),
          0,
          ctrl.signal
        );
        setItems(list || []);
        setHasMore(!!hasMore);
        setPage(0);
      } catch (e) {
        if (!isAbort(e)) console.error("autocomplete:", e);
        setItems([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    })();
    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debQ]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (!open || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 10) return;

    const next = page + 1;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    fetchPage((debQ ?? "").trim(), next, ctrl.signal)
      .then(({ items: list, hasMore }) => {
        setItems((prev) => [...prev, ...(list || [])]);
        setHasMore(!!hasMore);
        setPage(next);
      })
      .finally(() => setLoading(false));
  };

  const onFocus = () => {
    if (disabled) return;
    setOpen(true);
  };

  const onBlur = () => {
    // al salir, volvemos al label "oficial"
    setTimeout(() => setQ(value ?? ""), 120);
  };

  const handlePick = (item) => {
    onChange?.(item);
    setQ(item?.label ?? "");
    setOpen(false);
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <input
        type="text"
        placeholder={placeholder}
        value={q}
        disabled={disabled}
        onChange={(e) => {
          setQ(e.target.value); // NO llamamos onChange acá
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        className="w-full border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-600"
      />
      {open && (
        <div
          className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-72 overflow-auto"
          onScroll={onScroll}
        >
          {items.map((it, i) => (
            <button
              key={`${it.id ?? ""}-${i}`}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-gray-50"
              onMouseDown={(e) => {
                e.preventDefault();
                handlePick(it);
              }}
            >
              {String(it.label ?? "")}
            </button>
          ))}
          {loading && (
            <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              Sin resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ====================== Página ====================== */

const INITIAL_FORM = {
  codigo: "",
  clave: "",
  estado: "ACTIVO", // por defecto
  rolId: "",

  clienteId: null,
  clienteNombre: "",

  tarjetaId: null,
  tarjetaNumero: "",

  eessId: null,
  eessNombre: "",

  nombre: "",
  cargo: "",
};

export default function CrearUsuarios({ /* userId no se usa en alta */ onVolver = () => {} }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ====== opciones de ROL desde backend (carga completa) ======
  const [rolesOpts, setRolesOpts] = useState([]); // [{value, label}]
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesOverlayOpen, setRolesOverlayOpen] = useState(false);
  const [rolesOverlayMsg, setRolesOverlayMsg] = useState(
    "No se pudo cargar la lista completa de roles."
  );
  const [rolesReloadKey, setRolesReloadKey] = useState(0);

  // Cargar TODOS los roles para el combo
  useEffect(() => {
    let abort = false;

    const loadAllRoles = async () => {
      setRolesLoading(true);
      try {
        const pageSize = 200;
        let page = 0;
        let more = true;
        const acc = [];
        const maxPages = 100; // seguridad

        while (!abort && more && page < maxPages) {
          const url = apiRoutes.roles.autocompleteUrl("", page, pageSize);
          const data = await apiFetchJson(url);
          const pageItems = (data?.items ?? []).map((it) => ({
            value: String(it.id),
            label: String(it.label ?? ""),
          }));
          acc.push(...pageItems);
          more = !!data?.hasMore;
          page += 1;
        }

        if (abort) return;

        if (more) {
          // No llegamos a hasMore=false → mostramos overlay para reintentar
          setRolesOverlayOpen(true);
          setRolesOpts([]);
          return;
        }

        setRolesOpts(acc);
      } catch (e) {
        if (!abort) {
          setRolesOverlayMsg(
            e?.message || "No se pudo cargar la lista completa de roles."
          );
          setRolesOverlayOpen(true);
        }
      } finally {
        if (!abort) setRolesLoading(false);
      }
    };

    loadAllRoles();

    return () => {
      abort = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesReloadKey]);

  const retryLoadRoles = () => {
    setRolesOverlayOpen(false);
    setRolesReloadKey((k) => k + 1);
  };

  // ====== dirty ======
  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(INITIAL_FORM),
    [form]
  );

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const opcionesEstado = [
    { value: "ACTIVO", label: "ACTIVO" },
    { value: "INACTIVO", label: "INACTIVO" },
  ];

  // ====== guardar (POST /usuarios/agregar) ======
  const handleGuardar = async () => {
    if (!isDirty || saving) return;

    setSaving(true);
    setError("");

    try {
      const payload = {
        codigo: form.codigo || "",
        codigoClave: form.clave || "",
        nombreUsuario: form.nombre || "",
        cargoUsuario: form.cargo || "",
        activo: form.estado || "ACTIVO",
        tarjetaId: form.tarjetaId ? Number(form.tarjetaId) : null,
        clienteId: form.clienteId ? Number(form.clienteId) : null,
        eessId: form.eessId ? Number(form.eessId) : null,
        rolId: form.rolId ? Number(form.rolId) : null,
      };

      const created = await apiFetchJson(apiRoutes.usuarios.create, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // si querés, podrías mostrar un overlay de éxito;
      // por ahora solo volvemos y dejamos que la lista se refresque afuera
      console.log("[CrearUsuarios] Usuario creado:", created);
      onVolver();
    } catch (e) {
      console.error("[CrearUsuarios] Error al guardar", e);
      setError(e?.message || "No se pudo crear el usuario.");
    } finally {
      setSaving(false);
    }
  };

  /* ================== Fetchers paginados ================== */

  const fetchClientesPage = async (q, page, signal) => {
    const url = apiRoutes.clientes.autocompleteUrl({
      q: q || "",
      page: page ?? 0,
      size: PAGE_SIZE,
      tipo: "cliente",
      estadoId: 11,
      browse: !q,
    });
    const data = await apiFetchJson(url, { signal });
    const items = (data?.items ?? [])
      .map((x) => ({
        id: x.id,
        label: x.label ?? x.nombre ?? "",
      }))
      .filter((it) => !!it.label);
    return { items, hasMore: !!data?.hasMore };
  };

  const fetchEessPage = async (q, page, signal) => {
    const url = apiRoutes.eessHabilitadas.autocompleteUrl(
      "eess",
      q || "",
      page ?? 0,
      PAGE_SIZE
    );
    const data = await apiFetchJson(url, { signal });
    const items = (data?.items ?? [])
      .map((x) => ({
        id: x.id,
        label: x.label ?? x.nombre ?? "",
      }))
      .filter((it) => !!it.label);
    return { items, hasMore: !!data?.hasMore };
  };

  const fetchTarjetasPage = async (q, page, signal) => {
    if ((q ?? "").trim()) {
      const data = await apiFetchJson(
        apiRoutes.tarjetas.autocompleteUrl(
          "numero",
          q.trim(),
          page ?? 0,
          TARJ_PAGE_SIZE,
          29 // solo ACTIVAS
        ),
        { signal }
      );
      const items = (data?.items ?? [])
        .map((t) => ({
          id: t.id,
          label: t.numero ?? t.label ?? "",
        }))
        .filter((it) => !!it.label);
      return { items, hasMore: !!data?.hasMore };
    }

    // browse por lista cuando no hay q
    const data = await apiFetchJson(
      apiRoutes.tarjetas.listUrl({
        estadoIds: 29,
        page: page ?? 0,
        size: TARJ_PAGE_SIZE,
      }),
      { signal }
    );
    const items = (data?.items ?? [])
      .map((t) => ({
        id: t.id,
        label: t.numero ?? "",
      }))
      .filter((it) => !!it.label);
    const hasMore = items.length === TARJ_PAGE_SIZE;
    return { items, hasMore };
  };

  /* ====================== Render ====================== */
  return (
    <div className="card p-4">
      <div className="mb-2 flex items-center gap-2">
        <button type="button" className="btn-fil" onClick={onVolver}>
          <FaArrowLeft className="inline mr-2" /> Volver
        </button>
        <span className="page-title">Crear Usuario</span>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Introduzca los datos de Usuario
      </p>

      {error && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-md border border-gray-200">
        <div className="p-4">
          <Row>
            <Label text="Código*" />
            <Input
              name="codigo"
              value={form.codigo}
              onChange={onChange}
              className="w-40"
              disabled={saving}
            />
          </Row>

          <Row>
            <Label text="Clave*" />
            <Input
              name="clave"
              type="password"
              value={form.clave}
              onChange={onChange}
              className="w-72"
              disabled={saving}
            />
          </Row>

          <Row>
            <Label text="Estado:" />
            <Select
              name="estado"
              value={form.estado}
              onChange={onChange}
              options={opcionesEstado}
              className="w-56"
              disabled={saving}
            />
          </Row>

          {/* Rol (select con carga completa) */}
          <Row>
            <Label text="Rol:" />
            <RoleSelect
              name="rolId"
              value={form.rolId}
              onChange={(e) =>
                setForm((s) => ({ ...s, rolId: e.target.value || "" }))
              }
              options={rolesOpts}
              disabled={saving || rolesLoading}
              className="w-56"
            />
          </Row>

          {/* Overlay de error de carga completa de roles */}
          {rolesOverlayOpen && (
            <Overlay
              open={rolesOverlayOpen}
              title="Error al cargar roles"
              onClose={() => setRolesOverlayOpen(false)}
              actions={[
                { label: "Reintentar", onClick: retryLoadRoles },
                { label: "Cancelar", onClick: () => setRolesOverlayOpen(false) },
              ]}
            >
              <div className="text-sm text-gray-700">{rolesOverlayMsg}</div>
            </Overlay>
          )}

          {/* Cliente */}
          <Row>
            <Label text="Cliente:" />
            <AutoCombo
              value={form.clienteNombre}
              onChange={(item) =>
                setForm((s) => ({
                  ...s,
                  clienteId: item?.id ?? null,
                  clienteNombre: item?.label ?? "",
                }))
              }
              fetchPage={fetchClientesPage}
              placeholder="Buscar cliente…"
              className="w-[420px]"
              disabled={saving}
            />
          </Row>

          {/* Nro. de Tarjeta */}
          <Row>
            <Label text="Nro. de Tarjeta:" />
            <AutoCombo
              value={form.tarjetaNumero}
              onChange={(item) =>
                setForm((s) => ({
                  ...s,
                  tarjetaId: item?.id ?? null,
                  tarjetaNumero: item?.label ?? "",
                }))
              }
              fetchPage={fetchTarjetasPage}
              placeholder="Buscar tarjeta…"
              className="w-[420px]"
              disabled={saving}
            />
          </Row>

          {/* EE.SS. */}
          <Row>
            <Label text="EE.SS.:" />
            <AutoCombo
              value={form.eessNombre}
              onChange={(item) =>
                setForm((s) => ({
                  ...s,
                  eessId: item?.id ?? null,
                  eessNombre: item?.label ?? "",
                }))
              }
              fetchPage={fetchEessPage}
              placeholder="Buscar EE.SS…"
              className="w-[420px]"
              disabled={saving}
            />
          </Row>

          <Row>
            <Label text="Nombre:" />
            <Input
              name="nombre"
              value={form.nombre}
              onChange={onChange}
              className="w-[520px]"
              disabled={saving}
            />
          </Row>

          <Row>
            <Label text="Cargo:" />
            <Input
              name="cargo"
              value={form.cargo}
              onChange={onChange}
              className="w-[520px]"
              disabled={saving}
            />
          </Row>
        </div>

        <div className="border-t border-gray-200 px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            className={`btn-fil ${
              !isDirty || saving ? "opacity-60 cursor-not-allowed" : ""
            }`}
            onClick={handleGuardar}
            disabled={!isDirty || saving}
            title={
              !isDirty ? "Sin cambios" : saving ? "Guardando..." : "Guardar cambios"
            }
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Subcomponentes simples ---------- */
function Row({ children }) {
  return <div className="flex items-center mb-3">{children}</div>;
}

function Label({ text }) {
  return (
    <div className="w-48 pr-3 text-sm text-gray-700 flex justify-end">
      <span>{text}</span>
    </div>
  );
}

function Input({ className = "", type = "text", ...props }) {
  return (
    <input
      type={type}
      className={`border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-600 ${className}`}
      {...props}
    />
  );
}

function Select({ options = [], className = "", ...props }) {
  const opts = Array.isArray(options) ? options : [];
  return (
    <select
      className={`border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-600 ${className}`}
      {...props}
    >
      <option value="">-- Seleccionar --</option>
      {opts.map((op) => (
        <option key={op.value} value={op.value}>
          {op.label}
        </option>
      ))}
    </select>
  );
}

/* ---------- Select especializado para Rol ---------- */
function RoleSelect({ options = [], className = "", ...props }) {
  const opts = Array.isArray(options) ? options : [];
  return (
    <select
      className={`border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-600 ${className}`}
      {...props}
    >
      <option value="">-- Seleccionar --</option>
      {opts.map((op) => (
        <option key={op.value} value={op.value}>
          {op.label}
        </option>
      ))}
    </select>
  );
}
